/**
 * How often /desktop asks whether the workstation is ready yet.
 *
 * It used to be a flat 20 s. That is a reasonable poll for a machine nobody
 * is watching and a bad one for the moment somebody IS: the boot itself
 * finishes at some arbitrary instant, so on average half an interval — and at
 * worst the whole 20 s — is spent showing a "still setting up" card in front
 * of a workstation that is already serving. On a first visit that dead time
 * is indistinguishable from the boot being slow.
 *
 * So the cadence follows attention rather than a constant: quick while the
 * person is sitting there having just arrived, then easing off as the wait
 * turns into something they have walked away from. The endpoint is a cheap
 * status read, and the ladder converges to the old interval, so the cost is a
 * handful of extra reads in the first two minutes of a boot — paid only while
 * a boot is actually in progress, since the effect does not run otherwise.
 */

/** Steps are tried in order; the first whose window still contains `watchedMs` wins. */
export const BOOT_POLL_LADDER: ReadonlyArray<{ withinMs: number; everyMs: number }> = [
  // Just arrived, eyes on the card — a ready machine should show up as ready.
  { withinMs: 30_000, everyMs: 2_000 },
  // Still here, but this is now a wait rather than a moment.
  { withinMs: 2 * 60_000, everyMs: 5_000 },
];

/** Past the ladder: the original cadence, for a wait nobody is watching any more. */
export const BOOT_POLL_SLOWEST_MS = 20_000;

/**
 * Milliseconds to wait before the next boot-status poll.
 *
 * @param watchedMs how long this page has been watching THIS boot — not how
 *   long the machine has been booting. A person who opens /desktop ten
 *   minutes into a boot has still only just arrived, and their first seconds
 *   matter exactly as much as anyone's.
 */
export function bootPollDelayMs(watchedMs: number): number {
  const elapsed = Number.isFinite(watchedMs) && watchedMs > 0 ? watchedMs : 0;
  for (const step of BOOT_POLL_LADDER) {
    if (elapsed < step.withinMs) return step.everyMs;
  }
  return BOOT_POLL_SLOWEST_MS;
}
